import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai/index";
import { useTranslation } from "react-i18next";
import { computeFiscalYears } from "../common/fiscalPeriods";
import { ErrorResponse, postAuthenticationAPI } from "../datasources/api";
import { UserOperatingContextDTO } from "../datasources/api-client/data-contracts";
import { ApiRoutes } from "../datasources/networking/apiRoutes";
import { sendGet, sendPost } from "../datasources/networking/resources";
import { SelectWorkspacePayload } from "../datasources/requests/auth";
import { TokenResponse, WorkspacesResponse } from "../datasources/responses/auth";
import { currentAuthenticationToken, currentUser } from "../state/auth";
import { userPeriodFilter } from "../state/company";
import { makeSnackbarMessage, publishSnackbarMessage } from "../state/view";

const group = "post-auth-api";

/**
 * usePostAuthentication exposes all functions required for managing post-authentication workflows;
 * the currently supported post-authentication workflow is "select user workspace".
 */
export const usePostAuthentication = (autoFetch: boolean = true) => {
	const { t } = useTranslation("authentication");

	const client = useQueryClient();
	const initUserPeriodFilter = useSetAtom(userPeriodFilter);
	const publishMessage = useSetAtom(publishSnackbarMessage);
	const isEnabled = useRef<boolean>(autoFetch);

	const setUserData = useSetAtom(currentUser);
	const setAuthenticationToken = useSetAtom(currentAuthenticationToken);

	const {
		data,
		isLoading: isLoadingWorkspaces,
		error: getWorkspacesError,
		refetch,
	} = useQuery<WorkspacesResponse, ErrorResponse>({
		queryKey: [group, "get", "workspaces"],
		queryFn: async () => sendGet({ route: ApiRoutes.AuthWorkspaces }, postAuthenticationAPI),
		enabled: isEnabled.current,
	});

	const selectUserWorkspaceMutation = useMutation<
		TokenResponse & UserOperatingContextDTO,
		ErrorResponse,
		SelectWorkspacePayload
	>({
		mutationKey: [group, "select-workspace"],
		mutationFn: (request) =>
			sendPost(request, { route: ApiRoutes.AuthSignInSelectWorkspace }, postAuthenticationAPI),
	});

	const selectUserWorkspace = (workspaceId: string, onSuccess?: VoidFunction, onError?: VoidFunction) => {
		selectUserWorkspaceMutation.mutate(
			{ selected: workspaceId },
			{
				onSuccess: (response) => {
					setAuthenticationToken(response.token);
					const fiscalPeriodParts = response.company.fiscalPeriod.split("/");
					const fiscalYears = computeFiscalYears(fiscalPeriodParts[0], fiscalPeriodParts[1]);
					initUserPeriodFilter({
						start: fiscalYears[0].start,
						end: fiscalYears[0].end,
						selectedPeriodId: 0,
						type: "fiscal",
						fiscalYears,
					});

					setUserData(response);
					publishMessage(
						makeSnackbarMessage(t("welcome to {company}", { company: response.company.name }), "success")
					);
					onSuccess?.();
				},
				onError: (e) => {
					publishMessage(makeSnackbarMessage(e.message, "error"));
					onError?.();
				},
			}
		);
	};

	const reload = () => {
		isEnabled.current = true;
		client.invalidateQueries({ queryKey: [group], refetchType: "all" });
		refetch();
	};

	useEffect(() => {
		if (getWorkspacesError) {
			publishMessage(makeSnackbarMessage(getWorkspacesError.message, "error"));
		}
	}, [getWorkspacesError]);

	return {
		workspaces: data?.workspaces ?? [],
		isLoadingWorkspaces,
		isSelectingWorkspace: selectUserWorkspaceMutation.isPending,
		selectUserWorkspace,
		reload,
	};
};
